import type { RequestCookies } from "next/dist/compiled/@edge-runtime/cookies";
import type { OIDCConfig, OIDCUser, TokenClaims, TokenResponse } from "../oidc/types";
import { extractRolesFromClaims } from "../oidc/client";

export const OIDC_SESSION_COOKIE = "px_oidc_session";

export interface StoredSession {
  readonly refreshToken: string;
  readonly refreshExpiresAt: number;
  readonly user: Pick<OIDCUser, "id" | "encodedId" | "email" | "displayName" | "roles" | "profileComplete">;
}

export interface RefreshResult {
  readonly tokens: TokenResponse;
  readonly user: StoredSession["user"];
  readonly expiresAt: number;
}

let inflightRefresh: Promise<RefreshResult | null> | null = null;

/**
 * Parses refresh responses defensively so proxy/non-JSON upstream errors
 * return structured diagnostics instead of throwing parsing exceptions.
 */
async function parseRefreshResponse(response: Response): Promise<TokenResponse | Record<string, unknown>> {
  const raw = await response.text();
  if (!raw.trim()) {
    return {};
  }

  try {
    return JSON.parse(raw) as TokenResponse | Record<string, unknown>;
  } catch {
    return {
      error: "invalid_json_response",
      error_description: "Received non-JSON response from token endpoint",
      raw: raw.slice(0, 500),
    };
  }
}

/**
 * Reads the OIDC session from cookies.
 * @param cookieStore - The RequestCookies instance from next/headers cookies()
 */
export function readSession(cookieStore: RequestCookies): StoredSession | null {
  if (!cookieStore) {
    throw new Error("cookieStore is required - call cookies() from next/headers in your API route");
  }
  const store = cookieStore;
  const raw = store.get(OIDC_SESSION_COOKIE);
  if (!raw?.value) {
    console.log("[OIDC Session] No session cookie found");
    return null;
  }

  try {
    const parsed = JSON.parse(raw.value) as StoredSession;
    console.log("[OIDC Session] Read session cookie", {
      hasRefreshToken: !!parsed.refreshToken,
      hasRefreshExpiresAt: !!parsed.refreshExpiresAt,
      refreshExpiresAt: parsed.refreshExpiresAt ? new Date(parsed.refreshExpiresAt).toISOString() : null,
      isExpired: parsed.refreshExpiresAt ? parsed.refreshExpiresAt < Date.now() : null,
      hasUser: !!parsed.user,
      userId: parsed.user?.id || "(missing)",
      userEmail: parsed.user?.email || "(missing)",
      userRoles: parsed.user?.roles || [],
    profileComplete: parsed.user?.profileComplete ?? "(unknown)",
    });
    
    if (!parsed.refreshToken || !parsed.refreshExpiresAt || !parsed.user?.id) {
      console.warn("[OIDC Session] Session missing required fields", {
        hasRefreshToken: !!parsed.refreshToken,
        hasRefreshExpiresAt: !!parsed.refreshExpiresAt,
        hasUserId: !!parsed.user?.id,
      });
      return null;
    }
    if (parsed.refreshExpiresAt < Date.now()) {
      console.warn("[OIDC Session] Session refresh token expired", {
        refreshExpiresAt: new Date(parsed.refreshExpiresAt).toISOString(),
        now: new Date().toISOString(),
      });
      return null;
    }
    return parsed;
  } catch (error) {
    console.error("[OIDC Session] Failed to parse session cookie", error);
    return null;
  }
}

export function writeSession(
  session: StoredSession,
  options: {
    cookieStore: RequestCookies;
    sameSite?: "lax" | "strict" | "none";
  },
): void {
  if (!options?.cookieStore) {
    throw new Error("cookieStore is required - call cookies() from next/headers in your API route");
  }
  const store = options.cookieStore;
  const maxAgeSeconds = Math.max(60, Math.floor((session.refreshExpiresAt - Date.now()) / 1000));
  
  console.log("[OIDC Session] Writing session cookie", {
    userId: session.user.id,
    email: session.user.email,
    maxAgeSeconds,
    refreshExpiresAt: new Date(session.refreshExpiresAt).toISOString(),
    hasRefreshToken: !!session.refreshToken,
    profileComplete: session.user.profileComplete ?? "(unknown)",
  });
  
  store.set(OIDC_SESSION_COOKIE, JSON.stringify(session));
  
  console.log("[OIDC Session] ✅ Session cookie written");
}

export function clearSession(options: { cookieStore: RequestCookies }): void {
  if (!options?.cookieStore) {
    throw new Error("cookieStore is required - call cookies() from next/headers in your API route");
  }
  options.cookieStore.delete(OIDC_SESSION_COOKIE);
}

export async function refreshFromSession(options: {
  cookieStore: RequestCookies;
  config?: OIDCConfig;
}): Promise<RefreshResult | null> {
  if (!options?.cookieStore) {
    throw new Error("cookieStore is required - call cookies() from next/headers in your API route");
  }
  if (inflightRefresh) {
    return inflightRefresh;
  }

  inflightRefresh = (async () => {
    const session = readSession(options.cookieStore);
    if (!session) {
      console.log("[OIDC Session] No session found for refresh");
      return null;
    }

    console.log("[OIDC Session] Refreshing token", {
      userId: session.user.id,
      tokenEndpoint: options?.config?.tokenEndpoint || "(will use default)",
    });

    // Lazy import to avoid circular dependency issues
    const { createOIDCConfig } = await import("../oidc/config");
    const config = options?.config ?? createOIDCConfig();

    const body = new URLSearchParams({
      grant_type: "refresh_token",
      client_id: config.clientId,
      refresh_token: session.refreshToken,
    });

    const response = await fetch(config.tokenEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
      cache: "no-store",
    });

    const parsed = await parseRefreshResponse(response);
    if (!response.ok) {
      console.error("[OIDC Session] Token refresh failed", {
        status: response.status,
        statusText: response.statusText,
        error: parsed,
      });
      return null;
    }

    const tokens = parsed as TokenResponse;

    console.log("[OIDC Session] Token refresh successful", {
      userId: session.user.id,
      expiresIn: tokens.expires_in,
    });

    const refreshExpiresAt =
      Date.now() + 1000 * (typeof tokens.refresh_expires_in === "number" ? tokens.refresh_expires_in : 1800);

    // Refresh role claims from the fresh access token so role changes in Keycloak
    // are reflected without requiring full logout/login.
    let refreshedRoles = session.user.roles;
    try {
      const tokenParts = tokens.access_token.split(".");
      if (tokenParts.length === 3) {
        const decoded = atob(tokenParts[1].replace(/-/g, "+").replace(/_/g, "/"));
        const claims = JSON.parse(decoded) as TokenClaims;
        refreshedRoles = extractRolesFromClaims(claims);
      }
    } catch (error) {
      console.warn("[OIDC Session] Failed to decode refreshed token claims for roles", {
        userId: session.user.id,
        error: error instanceof Error ? error.message : String(error),
      });
    }

    writeSession(
      {
        refreshToken: tokens.refresh_token ?? session.refreshToken,
        refreshExpiresAt,
        user: {
          ...session.user,
          roles: refreshedRoles,
        },
      },
      { cookieStore: options.cookieStore },
    );

    const expiresAt = Date.now() + tokens.expires_in * 1000;

    return {
      tokens,
      user: {
        ...session.user,
        roles: refreshedRoles,
      },
      expiresAt,
    };
  })();

  return inflightRefresh.finally(() => {
    inflightRefresh = null;
  });
}

